import React, { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Star, SlidersHorizontal, X } from "lucide-react";
import { InnerNav } from "../components/InnerNav";
import { Overline } from "../components/motion";
import { useTranslate } from "../context/TranslateContext";
import { MOVIES, LANGUAGES, GENRES, YEARS, PLATFORMS_ALL } from "../mock/data";

const INDIAN_LANGS = ["Malayalam", "Hindi", "Tamil", "Telugu", "Kannada", "Bengali", "Rajasthani"];

export default function MoviesBrowse() {
  const { t } = useTranslate();
  const [language, setLanguage] = useState("All");
  const [year, setYear] = useState("All");
  const [genre, setGenre] = useState("All");
  const [platform, setPlatform] = useState("All");
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const filtered = useMemo(() => MOVIES.filter((m) => {
    if (language !== "All" && m.language !== language) return false;
    if (year !== "All" && m.year !== year) return false;
    if (genre !== "All" && !m.genre.toLowerCase().includes(genre.toLowerCase())) return false;
    if (platform !== "All" && !m.whereToWatch.some((w) => w.name === platform)) return false;
    return true;
  }), [language, year, genre, platform]);

  const reset = () => { setLanguage("All"); setYear("All"); setGenre("All"); setPlatform("All"); };
  const active = [language, year, genre, platform].filter((v) => v !== "All").length;

  return (
    <div className="min-h-screen bg-gradient-to-b from-paper to-surface">
      <InnerNav />
      <div className="mx-auto max-w-[1400px] px-6 py-14 md:px-12 md:py-16">
        <Overline>Browse the catalogue</Overline>
        <h1 className="mt-3 font-display text-5xl font-semibold tracking-tight text-cream md:text-7xl">{t("All ")}<span className="italic text-gradient-gold">{t("Films")}</span></h1>

        {/* Filters */}
        <div className="mt-10 flex flex-wrap items-center gap-4 rounded-2xl border border-edge bg-surface/40 p-4 md:p-6" data-testid="filters">
          <div className="flex items-center gap-2 font-body text-sm text-gold"><SlidersHorizontal className="h-4 w-4" /> {t("Filters")}</div>
          <Filter label="Language" value={language} setValue={setLanguage} options={["All", ...INDIAN_LANGS]} testId="filter-language" tr={t} />
          <Filter label="Year" value={year} setValue={setYear} options={["All", ...YEARS]} testId="filter-year" tr={t} />
          <Filter label="Genre" value={genre} setValue={setGenre} options={["All", ...GENRES]} testId="filter-genre" tr={t} />
          <Filter label="Streaming" value={platform} setValue={setPlatform} options={["All", ...PLATFORMS_ALL]} testId="filter-platform" tr={t} />
          {active > 0 && (
            <button onClick={reset} data-testid="filter-reset" className="inline-flex items-center gap-1.5 rounded-full border border-edge px-3 py-2 font-body text-sm text-ash hover:border-crimson hover:text-crimson">
              <X className="h-3.5 w-3.5" /> {t("Clear")} ({active})
            </button>
          )}
        </div>

        <div className="mt-6 font-body text-sm text-ash" data-testid="result-count">{filtered.length} {t("films")}</div>

        <div className="mt-4 grid grid-cols-2 gap-5 sm:grid-cols-3 lg:grid-cols-4">
          {filtered.map((m, i) => (
            <motion.div key={m.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}>
              <Link to={`/movie/${m.id}`} data-testid={`browse-movie-${m.id}`}>
                <motion.div whileHover={{ y: -6 }} className="group overflow-hidden rounded-2xl border border-edge bg-surface/40">
                  <div className="relative aspect-[2/3] overflow-hidden">
                    <img src={m.image} alt={m.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    <div className="absolute right-3 top-3 flex items-center gap-1 rounded-full bg-paper/85 px-2.5 py-1 backdrop-blur">
                      <Star className="h-3.5 w-3.5 fill-gold text-gold" /><span className="font-body text-sm text-cream">{m.rating}</span>
                    </div>
                    <div className="absolute left-3 top-3 rounded-full px-2.5 py-1 font-body text-xs" style={{ background: m.accent + "cc", color: "#0F0B0D" }}>{m.language}</div>
                  </div>
                  <div className="p-4">
                    <div className="font-display text-xl text-cream">{m.title}</div>
                    <div className="font-body text-xs text-ash">{m.year} · {t(m.genre)}</div>
                  </div>
                </motion.div>
              </Link>
            </motion.div>
          ))}
        </div>
        {filtered.length === 0 && <div className="py-20 text-center font-body text-ash" data-testid="no-films">{t("No films match these filters.")}</div>}
      </div>
    </div>
  );
}

const Filter = ({ label, value, setValue, options, testId, tr }) => (
  <div className="flex items-center gap-2">
    <span className="hidden font-body text-xs uppercase tracking-widest text-ash sm:inline">{tr(label)}</span>
    <select value={value} onChange={(e) => setValue(e.target.value)} data-testid={testId}
      className="rounded-full border border-edge bg-surface px-4 py-2 font-body text-sm text-cream focus:border-gold focus:outline-none">
      {options.map((o) => <option key={o} value={o} className="bg-surface">{o === "All" ? tr("All") : tr(o)}</option>)}
    </select>
  </div>
);
